import { client, authHeaders } from 'repositories/client';
import { getRequestPath } from 'utils/string';
import { paths } from 'repositories/distribution/paths';
import {
  CommentListResponse,
  InstallSessionResponse,
  ShowResponse,
} from 'repositories/distribution/responseType';

const show = async (accessKey: string): Promise<ShowResponse> => {
  const response = await client.get<ShowResponse>(
    getRequestPath(paths.show, { accessKey }),
    { headers: await authHeaders() }
  );
  return response.data;
};

const installSession = async (
  accessKey: string
): Promise<InstallSessionResponse> => {
  const response = await client.post<InstallSessionResponse>(
    getRequestPath(paths.installSession, { accessKey }),
    {},
    { headers: await authHeaders() }
  );
  return response.data;
};

const join = async (
  accessKey: string,
  key: string | null,
  displayName: string | null
): Promise<void> => {
  const params = new FormData();
  if (key) {
    params.append('key', key);
  }
  if (displayName) {
    params.append('display_name', displayName);
  }
  await client.post(getRequestPath(paths.join, { accessKey }), params, {
    headers: await authHeaders(),
  });
};

const leave = async (accessKey: string): Promise<void> => {
  await client.delete(getRequestPath(paths.leave, { accessKey }), {
    headers: await authHeaders(),
  });
};

const updateDisplayName = async (
  accessKey: string,
  displayName: string
): Promise<string> => {
  const params = new FormData();
  params.append('display_name', displayName);
  await client.put(getRequestPath(paths.displayName, { accessKey }), params, {
    headers: await authHeaders(),
  });

  return displayName;
};

const commentList = async (
  accessKey: string,
  page?: number
): Promise<CommentListResponse> => {
  const params: { [key: string]: string | number } = {};
  if (page) {
    params['page'] = page;
  }

  const response = await client.get<CommentListResponse>(
    getRequestPath(paths.comments, { accessKey }),
    {
      params,
      headers: await authHeaders(),
    }
  );
  return response.data;
};

const postComment = async (
  accessKey: string,
  message: string,
  replyTo?: number
): Promise<void> => {
  const params = new FormData();
  params.append('message', message);
  if (replyTo) {
    params.append('in_reply_to', String(replyTo));
  }
  await client.post(getRequestPath(paths.comments, { accessKey }), params, {
    headers: await authHeaders(),
  });
};

const deleteComment = async (
  accessKey: string,
  commentId: number
): Promise<void> => {
  await client.delete(
    getRequestPath(paths.commentDetail, { accessKey, id: commentId }),
    {
      headers: await authHeaders(),
    }
  );
};

const setNotification = async (
  accessKey: string,
  enable: boolean
): Promise<void> => {
  const params = new FormData();
  params.append('enable', enable ? 'true' : 'false');
  await client.put(getRequestPath(paths.notification, { accessKey }), params, {
    headers: await authHeaders(),
  });
};

export const distribution = {
  show,
  installSession,
  join,
  leave,
  updateDisplayName,
  commentList,
  postComment,
  deleteComment,
  setNotification,
};
